import { useEffect, useState } from "react";
import { apiFetch } from "../utility/apiFetch.js";
import * as Sentry from "@sentry/react";
import { Link } from "react-router-dom";
import Layout from "../layouts/Layout.jsx";
import Spinner from "../components/Spinner.jsx";
import InlineEdit from "../components/InlineEdit.jsx";
import NoDevices from "../components/NoDevices.jsx";
import GrowCard from "../components/GrowCard.jsx";
import InfoTooltip from "../components/InfoTooltip.jsx";
import Card from "../components/ui/Card.jsx";
import Badge from "../components/ui/Badge.jsx";
import EmptyState from "../components/ui/EmptyState.jsx";
import ErrorState from "../components/ui/ErrorState.jsx";
import { useGrowsQuery } from "../hooks/useGrowsQuery.js";
import { useRecipesQuery } from "../hooks/useRecipesQuery.js";
import { useInventoryQuery, useLowStockQuery } from "../hooks/useInventoryQuery.js";
import { useIsStale } from "../hooks/useIsStale.js";
import { formatDistanceToNow } from "date-fns";
import { computeVPD } from "../utility/vpd.js";
import usePlan from "../hooks/usePlan.js";

const API = import.meta.env.VITE_API_BASE_URL;

const METRIC_UNITS = { temperature: "°F", humidity: "%", co2: "ppm" };
const METRIC_LABELS = { temperature: "Temp", humidity: "RH", co2: "CO2" };

const METRIC_INFO = {
  temperature: {
    title: "Temperature",
    text: "Air temperature at the sensor, in °F. Drives colonization and fruiting speed — most species do best in the 65–75°F range.",
  },
  humidity: {
    title: "Relative Humidity (RH)",
    text: "Percentage of moisture in the air relative to the maximum it can hold. Fruiting typically wants 85–95% RH to keep caps from drying out.",
  },
  co2: {
    title: "Carbon Dioxide (CO₂)",
    text: "CO₂ concentration in ppm. High CO₂ favors colonization; fruiting needs fresh air exchange to bring it below ~1000 ppm.",
  },
  vpd: {
    title: "Vapor Pressure Deficit (VPD)",
    text: "How much drying power the air has, in kPa, derived from temperature and humidity. It sets evaporation off the mushroom surface — fruiting targets roughly 0.2–0.8 kPa.",
  },
};

const TYPE_LABELS = {
  liquid_culture: "Liquid Culture",
  bulk_spawn: "Bulk Spawn",
  substrate: "Substrate",
  other: "Other",
};
const TYPE_BADGE_COLOR = {
  liquid_culture: "info",
  bulk_spawn: "warning",
  substrate: "emerald",
  other: "gray",
};

function metricColor(value, limitLow, limitHigh) {
  if (value == null) return "text-gray-400";
  if (value < limitLow) return "text-blue-700";
  if (value > limitHigh) return "text-red-600";
  return "text-green-600";
}

function vpdColor(vpd) {
  if (vpd == null) return "text-gray-400";
  if (vpd < 0.1) return "text-blue-700";
  if (vpd < 0.5) return "text-green-600";
  if (vpd < 0.9) return "text-yellow-500";
  return "text-red-600";
}

function DeviceCard({ device, sensors, thresholds, saveName }) {
  const stale = useIsStale(device.last_seen);
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h3 className="text-sky-800 dark:text-sky-300 font-semibold flex items-center gap-2 min-w-0">
            <InlineEdit
              value={device.display_name}
              placeholder={device.hostname}
              onSave={name => saveName("device", device.id, name)}
            />
            <span className="text-xs font-mono text-gray-400 dark:text-gray-500 font-normal shrink-0">{device.connectcode}</span>
          </h3>
          <div className={`text-xs mt-0.5 ${stale ? "text-red-500 dark:text-red-400" : "text-gray-400 dark:text-gray-500"}`}>
            Updated {device.last_seen ? formatDistanceToNow(new Date(device.last_seen), { addSuffix: true }) : "never"}
          </div>
        </div>
        <Link
          to={`/dashboard/${device.id}`}
          className="text-sm text-sky-700 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 font-medium border border-sky-200 dark:border-sky-700 rounded px-3 py-1 hover:bg-sky-50 dark:hover:bg-sky-950 transition-colors shrink-0"
        >
          Details →
        </Link>
      </div>
      <div className="space-y-2">
        {sensors.map(sensor => {
          const isOrange = sensor.sensor_code === "orange1";
          const tempMetric = sensor.metrics.find(m => m.metric === "temperature");
          const humMetric = sensor.metrics.find(m => m.metric === "humidity");
          const co2Metric = sensor.metrics.find(m => m.metric === "co2");
          const vpd = (!stale && tempMetric?.latest_value != null && humMetric?.latest_value != null)
            ? computeVPD(tempMetric.latest_value, humMetric.latest_value) : null;
          return (
            <div key={sensor.id} className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-4 py-1.5 border-t border-gray-50 dark:border-gray-700">
              <div className="flex items-center gap-2 w-full sm:w-40 min-w-0 shrink">
                <span className={`w-2 h-2 rounded-full shrink-0 ${isOrange ? "bg-orange-500" : "bg-sky-500"}`} />
                <InlineEdit
                  value={sensor.display_name}
                  placeholder={sensor.sensor_name}
                  onSave={name => saveName("sensor", sensor.id, name)}
                  className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate"
                />
              </div>
              <div className="flex gap-1.5 sm:gap-5 ml-auto shrink-0">
                {["temperature", "humidity", "co2"].map(key => {
                  const metric = key === "temperature" ? tempMetric : key === "humidity" ? humMetric : co2Metric;
                  const t = thresholds[key];
                  const color = metric && t ? metricColor(metric.latest_value, t.low_value, t.high_value) : "text-gray-600 dark:text-gray-400";
                  return (
                    <div key={key} className="text-center w-14 sm:w-[58px] min-w-0 shrink-0">
                      {metric ? (
                        <>
                          <div className={`text-sm sm:text-base font-bold tabular-nums sm:whitespace-nowrap ${color}`}>
                            {!stale && metric.latest_value != null ? Math.round(metric.latest_value * 10) / 10 : "n/a"}
                            <span className="block sm:inline text-xs font-normal sm:ml-0.5">{METRIC_UNITS[key]}</span>
                          </div>
                          <div className="flex items-center justify-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                            {METRIC_LABELS[key]}
                            <InfoTooltip label={METRIC_INFO[key].title}>{METRIC_INFO[key].text}</InfoTooltip>
                          </div>
                        </>
                      ) : (
                        <div className="text-base font-bold">&nbsp;</div>
                      )}
                    </div>
                  );
                })}
                <div className="text-center w-14 sm:w-[58px] min-w-0 shrink-0">
                  {vpd != null ? (
                    <>
                      <div className={`text-base font-bold tabular-nums ${vpdColor(vpd)}`}>
                        {vpd.toFixed(2)}<span className="text-xs font-normal ml-0.5">kPa</span>
                      </div>
                      <div className="flex items-center justify-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                        VPD
                        <InfoTooltip label={METRIC_INFO.vpd.title}>{METRIC_INFO.vpd.text}</InfoTooltip>
                      </div>
                    </>
                  ) : (
                    <div className="text-base font-bold">&nbsp;</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function SectionHeader({ title, href, linkLabel, onDark }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className={onDark ? "text-sm font-semibold text-sky-200 uppercase tracking-wide" : "text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide"}>{title}</h2>
      {href && (
        <Link to={href} className={onDark ? "text-xs text-white hover:text-sky-200 font-medium" : "text-xs text-sky-700 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 font-medium"}>
          {linkLabel ?? "View all"} →
        </Link>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [dashLoading, setDashLoading] = useState(true);
  const [dashError, setDashError] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  const { data: grows } = useGrowsQuery();
  const { data: recipes } = useRecipesQuery();
  const { data: inventoryItems } = useInventoryQuery();
  const { data: lowStockItems } = useLowStockQuery();
  const { isPro, planLoading } = usePlan();

  const getDashboard = async () => {
    try {
      const res = await fetch(API + "dashboard", { credentials: "include" });
      if (res.status !== 200) { setDashError(await res.text()); return; }
      const data = await res.json();
      setDashboard(data || null);
      setDashLoading(false);
    } catch (e) {
      Sentry.captureException(e);
      setDashError(e.message);
    }
  };

  useEffect(() => {
    getDashboard();
    const es = new EventSource(API + "dashboardlive", { withCredentials: true });
    es.addEventListener("metrics", (e) => {
      const updates = JSON.parse(e.data);
      setDashboard(prev => {
        if (!prev) return prev;
        const next = structuredClone(prev);
        updates.forEach(u => {
          const d = next.devices[u.device_id];
          if (!d) return;
          d.device.last_seen = u.last_seen;
          u.sensors.forEach(su => {
            const s = d.sensors.find(s => s.id === su.id);
            if (!s) return;
            su.metrics.forEach(mu => {
              const m = s.metrics.find(m => m.metric === mu.metric);
              if (m) m.latest_value = mu.latest_value;
            });
          });
        });
        return next;
      });
    });
    return () => es.close();
  }, []);

  const saveName = async (type, id, name) => {
    const path = type === "device" ? `devices/${id}/name` : `sensors/${id}/name`;
    await apiFetch(path, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }) });
    setDashboard(prev => {
      const next = structuredClone(prev);
      if (type === "device") {
        Object.values(next.devices).forEach(d => { if (d.device.id === id) d.device.display_name = name || null; });
      } else {
        Object.values(next.devices).forEach(d => d.sensors.forEach(s => { if (s.id === id) s.display_name = name || null; }));
      }
      return next;
    });
  };

  if (dashLoading) return <Layout title="Dashboard"><Spinner /></Layout>;
  if (dashError) return <Layout title="Dashboard"><ErrorState>{dashError}</ErrorState></Layout>;

  const thresholds = dashboard ? (Object.values(dashboard.devices ?? {})[0]?.thresholds?.thresholds || {}) : {};
  // Dashboard grows: ALL favorited grows are shown (any status — a favorited completed
  // grow stays pinned here), uncapped. Then up to ACTIVE_PREVIEW active non-favorites
  // fill in, with a "+N more" link for the rest. Favorites are always fully visible.
  const ACTIVE_PREVIEW = 2;
  const allGrows = grows ?? [];
  const favoriteGrows = allGrows.filter(g => g.is_favorite);
  const activeNonFavorite = allGrows.filter(g => g.status === "active" && !g.is_favorite);
  const dashboardGrows = [...favoriteGrows, ...activeNonFavorite.slice(0, ACTIVE_PREVIEW)];
  const extraActiveCount = Math.max(0, activeNonFavorite.length - ACTIVE_PREVIEW);
  const latestRecipe = (recipes ?? []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

  function liveDeviceFor(grow) {
    if (!grow.device_id || !dashboard?.devices) return null;
    return dashboard.devices[grow.device_id] ?? null;
  }

  return (
    <Layout title="Dashboard">
      <div className="container w-full space-y-8">

        {/* Sensors */}
        <section>
          <SectionHeader title="Sensors" href="/dashboard/devices" linkLabel="All devices" onDark />
          {!dashboard || Object.keys(dashboard.devices ?? {}).length === 0 ? (
            <NoDevices refreshDashboards={getDashboard} />
          ) : (
            <div className="space-y-3">
              {Object.values(dashboard.devices).map(({ device, sensors }) => (
                <DeviceCard key={device.id} device={device} sensors={sensors} thresholds={thresholds} saveName={saveName} />
              ))}
            </div>
          )}
        </section>

        {/* Grows — favorites first, then active */}
        <section>
          <SectionHeader title="Grows" href="/grows" />
          {planLoading ? (
            <Card className="p-6 text-center text-gray-300 text-sm animate-pulse">Loading…</Card>
          ) : !isPro ? (
            <Card className="p-6">
              <EmptyState
                className="py-0"
                icon="🌱"
                title="Track your grows with Pro"
                description={
                  <>
                    Upgrade to Pro to log grows, track phases, and record harvests.
                    To upgrade, email{" "}
                    <a href="mailto:hello@fungistics.com" className="text-brand underline">hello@fungistics.com</a>.
                  </>
                }
                action={
                  <Link to="/grows" className="text-xs text-brand hover:text-brand-hover font-medium">
                    View grows →
                  </Link>
                }
              />
            </Card>
          ) : dashboardGrows.length === 0 ? (
            <Card className="p-6">
              <EmptyState description={<>No active grows · <Link to="/grows/new" className="text-brand hover:underline">Start one</Link></>} />
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dashboardGrows.map(grow => (
                <GrowCard key={grow.id} grow={grow} liveDevice={liveDeviceFor(grow)} hostname={liveDeviceFor(grow)?.device?.hostname} />
              ))}
              {extraActiveCount > 0 && (
                <Link to="/grows" className="flex items-center justify-center rounded-xl border-2 border-dashed border-sky-200 dark:border-sky-800 text-sky-700 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-950 text-sm font-medium p-6 transition-colors">
                  +{extraActiveCount} more active {extraActiveCount === 1 ? "grow" : "grows"} →
                </Link>
              )}
            </div>
          )}
        </section>

        {/* Low stock alert */}
        {lowStockItems && lowStockItems.length > 0 && (
          <section>
            <SectionHeader title="Low Stock" href="/inventory" linkLabel="View inventory" />
            <Card padding="none" className="divide-y divide-gray-50 dark:divide-gray-700">
              {lowStockItems.map((item) => (
                <Link key={item.id} to={`/inventory/${item.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-amber-50 dark:hover:bg-amber-950 transition-colors">
                  <div>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{item.name}</span>
                    {item.species && <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">{item.species}</span>}
                  </div>
                  <span className="text-sm font-semibold text-warning tabular-nums">
                    {item.quantity} {item.unit} left
                  </span>
                </Link>
              ))}
            </Card>
          </section>
        )}

        {/* Latest Recipe + Analytics + Inventory teasers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <section>
            <SectionHeader title="Latest Recipe" href="/recipes" />
            {planLoading ? (
              <Card className="p-6 text-center text-gray-300 text-sm animate-pulse">Loading…</Card>
            ) : !isPro ? (
              <Card className="p-6">
                <EmptyState
                  className="py-0"
                  icon="📋"
                  title="Save recipes with Pro"
                  description={
                    <>
                      Upgrade to Pro to build a recipe library with ingredients and procedures.
                      Email{" "}
                      <a href="mailto:hello@fungistics.com" className="text-brand underline">hello@fungistics.com</a> to upgrade.
                    </>
                  }
                  action={
                    <Link to="/recipes" className="text-xs text-brand hover:text-brand-hover font-medium">
                      View recipes →
                    </Link>
                  }
                />
              </Card>
            ) : !latestRecipe ? (
              <Card className="p-6">
                <EmptyState description={<>No recipes yet · <Link to="/recipes/new" className="text-brand hover:underline">Create one</Link></>} />
              </Card>
            ) : (
              <Link to={`/recipes/${latestRecipe.id}`} className="block">
                <Card className="hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-gray-800 dark:text-gray-100">{latestRecipe.name}</div>
                      {latestRecipe.species && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{latestRecipe.species}</div>
                      )}
                    </div>
                    <Badge color={TYPE_BADGE_COLOR[latestRecipe.recipe_type] ?? "gray"} className="shrink-0">
                      {TYPE_LABELS[latestRecipe.recipe_type] ?? latestRecipe.recipe_type}
                    </Badge>
                  </div>
                  {latestRecipe.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">{latestRecipe.description}</p>
                  )}
                </Card>
              </Link>
            )}
          </section>

          <section>
            <SectionHeader title="Analytics" href="/analytics" linkLabel="Open" />
            <Link to="/analytics" className="block">
              <Card className="flex flex-col justify-between hover:shadow-md transition-shadow min-h-[80px]">
                <div className="text-sm text-gray-500 dark:text-gray-400">Historical sensor trends and insights</div>
                <div className="mt-3 text-xs text-brand dark:text-sky-400 font-medium">View analytics →</div>
              </Card>
            </Link>
          </section>

          <section>
            <SectionHeader title="Inventory" href="/inventory" />
            <Link to="/inventory" className="block">
              <Card className="flex flex-col justify-between hover:shadow-md transition-shadow min-h-[80px]">
                <div className="flex items-center gap-3">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-800 dark:text-gray-100 tabular-nums">{inventoryItems?.length ?? "—"}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">items</div>
                  </div>
                  {lowStockItems && lowStockItems.length > 0 && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-amber-500 tabular-nums">{lowStockItems.length}</div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">low stock</div>
                    </div>
                  )}
                </div>
                <div className="mt-3 text-xs text-brand dark:text-sky-400 font-medium">Manage inventory →</div>
              </Card>
            </Link>
          </section>
        </div>

      </div>
    </Layout>
  );
}
